import React, { useState, useEffect, useRef } from 'react';
import SuperPhilosophyTagline from '../../components/SuperPhilosophyTagline';
import QuoteTool from '../../components/QuoteTool';
import LaunchBanner from "../../components/LaunchBanner";

const BookstorePartnership = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (el) => { sectionRefs.current[id] = el; };
  const isVisible = (id) => visibleSections.has(id);
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fadeIn = (id, delay = 0) => ({
    opacity: isVisible(id) ? 1 : 0,
    transform: isVisible(id) ? 'translateY(0)' : 'translateY(30px)',
    transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
  });

  const T = {
    pageBg: '#f0f4f8',
    heroBg: 'linear-gradient(170deg, #1e3a5f 0%, #2d4a7a 60%, #3b5998 100%)',
    heroText: '#ffffff',
    heroSub: 'rgba(255,255,255,0.85)',
    heading: '#1e293b',
    body: '#475569',
    bodyLight: '#64748b',
    accent: '#4f46e5',
    accentLight: '#eef2ff',
    accentBorder: '#c7d2fe',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    sectionAlt: '#f8fafc',
    green: '#059669',
    greenLight: '#d1fae5',
    gold: '#d97706',
    goldLight: '#fef3c7',
  };

  return (
    <div style={{ background: T.pageBg, minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* HERO */}
      <section style={{ background: T.heroBg, color: T.heroText, padding: '80px 24px 100px', textAlign: 'center' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', padding: '8px 20px', borderRadius: 999, fontSize: 14, fontWeight: 600, marginBottom: 24, letterSpacing: 0.5 }}>
            📖 CHRISTIAN BOOKSTORE PARTNER PROGRAM
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800, margin: '0 0 24px', lineHeight: 1.1 }}>
            Bible Sales Are Up. Bookstores Are Closing.<br />
            <span style={{ color: '#fbbf24' }}>There's a Solution.</span>
          </h1>
          <div style={{ marginBottom: '24px' }}>
            <SuperPhilosophyTagline variant="partnership" color="#fff" />
          </div>
          <p style={{ fontSize: 'clamp(18px, 2vw, 22px)', color: T.heroSub, maxWidth: 800, margin: '0 auto 40px', lineHeight: 1.5 }}>
            Turn your existing customer database into a recurring revenue stream — with zero inventory, zero shipping, and zero added workload for your staff.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => scrollTo('how-it-works')} style={{ background: '#fbbf24', color: '#1e3a5f', border: 'none', padding: '16px 32px', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(251, 191, 36, 0.4)' }}>
              See How It Works
            </button>
            <button onClick={() => scrollTo('apply')} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', padding: '14px 32px', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(10px)' }}>
              Become a Partner
            </button>
          </div>
        </div>
      </section>

      <LaunchBanner />

      {/* MARKET REALITY */}
      <section ref={setSectionRef('market')} id="market" style={{ padding: '80px 24px', background: T.cardBg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', ...fadeIn('market') }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: T.accent, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, marginBottom: 12 }}>THE MARKET REALITY</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              The Bible Market Is Booming
            </h2>
            <p style={{ fontSize: 18, color: T.body, maxWidth: 700, margin: '0 auto' }}>
              While general book retail declines, faith-based reading is experiencing unprecedented growth.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, marginBottom: 40 }}>
            <div style={{ background: T.accentLight, border: `2px solid ${T.accentBorder}`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: T.accent, marginBottom: 8 }}>13.7M</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.heading, marginBottom: 8 }}>NEW BIBLES SOLD</div>
              <div style={{ fontSize: 14, color: T.bodyLight }}>In the US in 2024 — a 22% increase year-over-year</div>
            </div>
            <div style={{ background: T.greenLight, border: `2px solid #6ee7b7`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: T.green, marginBottom: 8 }}>200M+</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.heading, marginBottom: 8 }}>EXISTING BIBLE OWNERS</div>
              <div style={{ fontSize: 14, color: T.bodyLight }}>In the US who own Bibles but struggle to apply scripture</div>
            </div>
            <div style={{ background: T.goldLight, border: `2px solid #fcd34d`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: T.gold, marginBottom: 8 }}>44%</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.heading, marginBottom: 8 }}>GEN Z CURIOUS</div>
              <div style={{ fontSize: 14, color: T.bodyLight }}>About Jesus and the Bible — first-time Bible buyers surging</div>
            </div>
          </div>

          <div style={{ background: T.sectionAlt, borderLeft: `4px solid ${T.accent}`, padding: 24, borderRadius: 8, fontSize: 16, color: T.body, lineHeight: 1.6 }}>
            <strong style={{ color: T.heading }}>Your customers are buying Bibles in record numbers.</strong> But ownership doesn't equal application. Millions struggle to translate scripture into daily life. That gap is your opportunity — and our platform fills it.
          </div>
        </div>
      </section>

      {/* WHY THIS WORKS */}
      <section ref={setSectionRef('why')} id="why" style={{ padding: '80px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', ...fadeIn('why') }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: T.green, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, marginBottom: 12 }}>WHY THIS WORKS FOR YOUR STORE</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              A Pure Profit Module
            </h2>
            <p style={{ fontSize: 18, color: T.body, maxWidth: 700, margin: '0 auto' }}>
              While other bookstores struggle with inventory costs and declining sales, you'll have a recurring revenue stream that requires nothing from you but a customer list and an email send.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {[
              { icon: '📦', title: 'Zero Inventory', text: 'No books to stock, ship, or warehouse. Nothing physical changes hands.' },
              { icon: '💰', title: 'Zero Upfront Cost', text: 'No investment required. No setup fees. No risk to your business.' },
              { icon: '⏱️', title: 'Zero Staff Workload', text: 'Send the email we provide. That\'s it. Your team continues their normal work.' },
              { icon: '🔁', title: 'Recurring Revenue', text: '35% commission on every subscription — for as long as your referred customer subscribes.' },
              { icon: '📊', title: 'Partner Dashboard', text: 'Real-time tracking of your referrals, conversions, and earnings.' },
              { icon: '✨', title: 'Trusted Solution', text: 'You\'re recommending a tool that genuinely helps your customers grow in their faith.' }
            ].map((item, i) => (
              <div key={i} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 16, padding: 28, transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>{item.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: T.heading, marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontSize: 15, color: T.body, lineHeight: 1.5 }}>{item.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section ref={setSectionRef('how-it-works')} id="how-it-works" style={{ padding: '80px 24px', background: T.cardBg }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', ...fadeIn('how-it-works') }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: T.accent, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, marginBottom: 12 }}>HOW IT WORKS</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              From Signup to Revenue in 4 Steps
            </h2>
          </div>

          {[
            { num: '01', title: 'Apply to Become a Partner', text: 'Fill out our partner application. Once approved, you\'ll receive a unique partner code, marketing materials, and access to your dashboard.' },
            { num: '02', title: 'Email Your Customer Database', text: 'We provide professionally written email templates and graphics. Customize them with your store\'s voice, then send to your list. Your unique link tracks every signup.' },
            { num: '03', title: 'Customers Subscribe', text: 'Your customers click the link, learn about the platform, and subscribe. The system automatically tracks them as your referrals.' },
            { num: '04', title: 'You Earn 35% — Forever', text: 'You receive 35% of every subscription payment for as long as that customer remains subscribed. Track everything in your partner dashboard.' }
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 24, marginBottom: 32, alignItems: 'flex-start' }}>
              <div style={{ flexShrink: 0, width: 64, height: 64, background: T.accent, color: '#fff', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800 }}>
                {step.num}
              </div>
              <div style={{ flex: 1, paddingTop: 8 }}>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: T.heading, margin: '0 0 8px' }}>{step.title}</h3>
                <p style={{ fontSize: 16, color: T.body, lineHeight: 1.6, margin: 0 }}>{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* THE PRODUCT */}
      <section ref={setSectionRef('product')} id="product" style={{ padding: '80px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', ...fadeIn('product') }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: T.gold, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, marginBottom: 12 }}>WHAT YOU'RE RECOMMENDING</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              The Best Bible Study Buddy
            </h2>
            <p style={{ fontSize: 18, color: T.body, maxWidth: 800, margin: '0 auto', lineHeight: 1.6 }}>
              Emit is an AI-powered Biblical wisdom coach that helps your customers move beyond reading scripture to actually living it — every day, in real situations they face.
            </p>
          </div>

          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 20, padding: 40, marginBottom: 32 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
              <div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: T.heading, margin: '0 0 16px' }}>📖 What Emit Does</h3>
                <ul style={{ margin: 0, paddingLeft: 20, color: T.body, lineHeight: 1.8, fontSize: 15 }}>
                  <li>Provides personalized Biblical wisdom for daily decisions</li>
                  <li>Answers questions about scripture with depth and context</li>
                  <li>Helps users apply Biblical principles to real-life challenges</li>
                  <li>Available 24/7 — no waiting for a pastor or study group</li>
                  <li>Strengthens spiritual disciplines through daily engagement</li>
                </ul>
              </div>
              <div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: T.heading, margin: '0 0 16px' }}>✨ Why Customers Love It</h3>
                <ul style={{ margin: 0, paddingLeft: 20, color: T.body, lineHeight: 1.8, fontSize: 15 }}>
                  <li>Bridges the gap between owning a Bible and living scripture</li>
                  <li>Honors their faith without watering down theology</li>
                  <li>Affordable — fraction of a typical seminary class or course</li>
                  <li>Complements (not replaces) their church and study group</li>
                  <li>Includes wellness, family, and performance coaches too</li>
                </ul>
              </div>
            </div>
          </div>

          <div style={{ background: T.accentLight, border: `2px solid ${T.accentBorder}`, borderRadius: 16, padding: 24, textAlign: 'center', fontSize: 16, color: T.heading, lineHeight: 1.6 }}>
            <strong>Your customers already love the Bible.</strong> Now you can give them a tool that helps them live it — and earn revenue every month they continue their subscription.
          </div>
        </div>
      </section>

      {/* COMMISSION CLARITY */}
      <section ref={setSectionRef('commission')} id="commission" style={{ padding: '80px 24px', background: T.cardBg }}>
        <div style={{ maxWidth: 900, margin: '0 auto', ...fadeIn('commission') }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ color: T.green, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, marginBottom: 12 }}>THE NUMBERS</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              35% Commission. Recurring. Forever.
            </h2>
            <p style={{ fontSize: 18, color: T.body, maxWidth: 700, margin: '0 auto' }}>
              No tiers. No fine print. No expiration. As long as your referred customer subscribes, you earn 35%.
            </p>
          </div>

          <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a7a 100%)', color: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 600, opacity: 0.85, marginBottom: 8, letterSpacing: 1 }}>EXAMPLE: 100 CUSTOMERS SUBSCRIBE AT $20/MONTH</div>
            <div style={{ fontSize: 'clamp(40px, 6vw, 56px)', fontWeight: 800, color: '#fbbf24', margin: '8px 0' }}>$700/month</div>
            <div style={{ fontSize: 16, opacity: 0.9, maxWidth: 500, margin: '12px auto 0', lineHeight: 1.5 }}>
              In recurring monthly revenue — with zero inventory, zero shipping, and zero ongoing workload from your team. Scale to 500 subscribers = $3,500/month. Scale to 1,000 = $7,000/month.
            </div>
          </div>
        </div>
      </section>

      {/* APPLY */}
      <section ref={setSectionRef('apply')} id="apply" style={{ padding: '80px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: 800, margin: '0 auto', ...fadeIn('apply') }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: T.heading, margin: '0 0 16px', lineHeight: 1.2 }}>
              Ready to Become a Partner?
            </h2>
            <p style={{ fontSize: 18, color: T.body, maxWidth: 600, margin: '0 auto' }}>
              Apply below and we'll reach out within 2 business days to set up your partner account, provide your code, and share your marketing materials.
            </p>
          </div>

          <QuoteTool partnershipType="bookstore" />
        </div>
      </section>

      {/* FOOTER */}
      <section style={{ padding: '40px 24px', background: '#1e3a5f', color: '#fff', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <p style={{ fontSize: 14, opacity: 0.85, margin: 0 }}>
            FHO Platform — MindStreams Technology LLC<br />
            www.freehappyone.com · partners@freehappyone.com
          </p>
        </div>
      </section>

    </div>
  );
};

export default BookstorePartnership;
